import { ApiResponse, ApiService } from './api.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import {
  Map,
  MapEvents,
  MapHeightGroups,
  MapLevels,
  MapOrganizations,
} from '../models/map';

import { ImageService } from './image.service';
import { SyncStatusService } from './sync-status.service';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SeriesMapDataService
  extends SyncStatusService
  implements OnDestroy
{
  public maps: Observable<Map[]>;
  public tags: Observable<string[]>;
  public judges: Observable<string[]>;
  public designers: Observable<string[]>;
  public organizations: Observable<string[]>;
  public hosts: Observable<string[]>;
  public levels: Observable<string[]>;
  public events: Observable<string[]>;
  public height_groups: Observable<string[]>;

  private URL = 'series-maps';
  private _loaded = false;
  private _maps: BehaviorSubject<Map[]>;
  private _tags: BehaviorSubject<string[]>;
  private _judges: BehaviorSubject<string[]>;
  private _designers: BehaviorSubject<string[]>;
  private _organizations: BehaviorSubject<string[]>;
  private _hosts: BehaviorSubject<string[]>;
  private _levels: BehaviorSubject<string[]>;
  private _events: BehaviorSubject<string[]>;
  private _height_groups: BehaviorSubject<string[]>;
  private data: {
    maps: Map[];
    tags: string[];
    judges: string[];
    designers: string[];
    organizations: string[];
    hosts: string[];
    levels: string[];
    events: string[];
    height_groups: string[];
    updated: Set<string>;
  };
  private _destroy = new Subject<void>();
  private _synchronizedOriginalMaps = false;

  constructor(
    private api: ApiService,
    private imageService: ImageService,
  ) {
    super();
    this.data = {
      maps: [],
      tags: [],
      judges: [],
      designers: [],
      organizations: MapOrganizations.map((org) => org.name),
      hosts: [],
      levels: MapLevels,
      events: MapEvents,
      height_groups: MapHeightGroups,
      updated: new Set(),
    };
    this._maps = new BehaviorSubject([]);
    this.maps = this._maps.asObservable();
    this._tags = new BehaviorSubject([]);
    this.tags = this._tags.asObservable();
    this._judges = new BehaviorSubject([]);
    this.judges = this._judges.asObservable();
    this._designers = new BehaviorSubject([]);
    this.designers = this._designers.asObservable();
    this._organizations = new BehaviorSubject([]);
    this.organizations = this._organizations.asObservable();
    this._hosts = new BehaviorSubject([]);
    this.hosts = this._hosts.asObservable();
    this._levels = new BehaviorSubject([]);
    this.levels = this._levels.asObservable();
    this._events = new BehaviorSubject([]);
    this.events = this._events.asObservable();
    this._height_groups = new BehaviorSubject([]);
    this.height_groups = this._height_groups.asObservable();

    this.maps.pipe(takeUntil(this._destroy)).subscribe((maps) => {
      this.data.tags = [];
      maps.map((map) => {
        map.tags.map((tag: string) => {
          if (this.data.tags.indexOf(tag) < 0) {
            this.data.tags.push(tag);
          }
        });
        map.judges.map((judge: string) => {
          if (this.data.judges.indexOf(judge) < 0) {
            this.data.judges.push(judge);
          }
        });
        map.designers.map((designer: string) => {
          if (this.data.designers.indexOf(designer) < 0) {
            this.data.designers.push(designer);
          }
        });
        if (map.organization) {
          const organization = map.organization;
          if (this.data.organizations.indexOf(organization) < 0) {
            this.data.organizations.push(organization);
          }
        }
        if (map.host) {
          const host = map.host;
          if (this.data.hosts.indexOf(host) < 0) {
            this.data.hosts.push(host);
          }
        }
        if (map.level) {
          const level = map.level;
          if (this.data.levels.indexOf(level) < 0) {
            this.data.levels.push(level);
          }
        }
        if (map.event) {
          const event = map.event;
          if (this.data.events.indexOf(event) < 0) {
            this.data.events.push(event);
          }
        }
        if (map.height_group) {
          const height_group = map.height_group;
          if (this.data.height_groups.indexOf(height_group) < 0) {
            this.data.height_groups.push(height_group);
          }
        }
      });
      this._publishDerived();
    });
    this._publishDerived(); // push out static data even when no maps yet
    this._publish();
  }

  private _publishDerived() {
    this._publishTags();
    this._publishJudges();
    this._publishDesigners();
    this._publishOrganizations();
    this._publishHosts();
    this._publishLevels();
    this._publishEvents();
    this._publishHeightGroups();
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._clearSyncTimeout();
  }

  mapsExist(): boolean {
    return this.data.maps.length > 0;
  }

  byId(id: string): Map {
    return this.data.maps.find((m) => m.id === id);
  }

  async load(force = false): Promise<Array<Map>> {
    if (this._loaded && !force) {
      return Promise.resolve([...this.data.maps]);
    }
    const dbLoadStart: number = new Date().getTime();
    try {
      const docs: ApiResponse = await this.api.allDocs(this.URL);
      this._lastSync = docs.until;
      this.data.maps = docs.results.map((payload) =>
        this._payloadToInstance(payload),
      );
      this._loaded = true;
      this._publish();

      const delta = new Date().getTime() - dbLoadStart;
      const numMaps = (this.data.maps && this.data.maps.length) || 0;
      console.debug('Database', delta, 'Load', 'SeriesMaps');
      console.debug('Num SeriesMaps', numMaps);
    } catch (e) {
      console.error(e);
      throw e;
    }
    return this.data.maps;
  }

  override async liveSync(): Promise<void> {
    await super.liveSync();
    this._synchronizeOriginalMaps();
  }

  public loadById(id: string): Promise<Map> {
    return this.api
      .get(this.URL, id)
      .then((p) => this._payloadToInstance(p as Map));
  }

  public getById(id: string): Map {
    return this.data.maps.find((obj) => obj.id === id);
  }

  public unload(): void {
    this.data.maps = [];
    this._publish();
  }

  protected async _getLatestFromApi(): Promise<Array<Map>> {
    const docs: ApiResponse = await this.api.allDocs(this.URL, this._lastSync);
    this._lastSync = docs.until;
    return docs.results;
  }

  protected _addUpdate(payload: Map) {
    const map: Map = this._payloadToInstance(payload);
    this._processChange(map);
    return map;
  }

  protected _publish(): void {
    // new object list to trigger change detection and isolate changes.
    if (!this._maps.closed) {
      this._maps.next([...this.data.maps]);
    }
  }

  private _payloadToInstance(map: Map): Map {
    return new Map({ ...map });
  }

  private _processChange(map: Map): Map {
    this.data.maps = [...this.data.maps.filter((m) => m.id !== map.id), map];
    return map;
  }

  private _broadcastDelete(id: string): void {
    this.data.maps = this.data.maps.filter((map) => map.id !== id);
    this._publish();
  }

  private _publishTags(): void {
    this._tags.next([...this.data.tags]);
  }

  private _publishJudges(): void {
    this._judges.next([...this.data.judges]);
  }

  private _publishDesigners(): void {
    this._designers.next([...this.data.designers]);
  }

  private _publishOrganizations(): void {
    this._organizations.next([...this.data.organizations]);
  }

  private _publishHosts(): void {
    this._hosts.next([...this.data.hosts]);
  }

  private _publishLevels() {
    this._levels.next([...this.data.levels]);
  }

  private _publishEvents() {
    this._events.next([...this.data.events]);
  }

  private _publishHeightGroups() {
    this._height_groups.next([...this.data.height_groups]);
  }

  private async _synchronizeOriginalMaps(): Promise<void> {
    if (!this._synchronizedOriginalMaps) {
      this._maps.getValue().forEach(async (map: Map) => {
        try {
          const url = map.originalImageURL;
          if (url) {
            await this.imageService.fetchImage(url);
          }
        } catch (e) {
          console.error(e);
        }
      });
      this._synchronizedOriginalMaps = true;
    }
  }
}
